/**
 * PaginatedPaper — multi-page letter sheet for the Tiptap editor.
 *
 * Phase 5 of the editor rebuild. The legacy `Paper` component
 * renders a single 8.5" × 11" sheet and clips overflow; legacy
 * `PagedDocument` solves multi-page rendering by packing
 * pre-measured sections into separate Paper instances. Neither
 * works for a single Tiptap contenteditable, so this component:
 *
 *   - Wraps the Tiptap content in a single editable surface
 *   - Measures the content's scrollHeight on every change
 *   - Grows the sheet in 11" page-units as the content gets taller
 *   - Repeats brand chrome (logo top-left, office ribbon bottom-
 *     left) at each page boundary so the visual stays branded
 *     across all pages — chrome lives in the LEFT MARGIN strip,
 *     so it never overlaps the editor's content column
 *   - Renders subtle perforation lines between pages so the user
 *     sees where each printed page will break
 *
 * Trade-off (v1): the editor content flows continuously through
 * the right column, so a bullet can visually cross a page
 * boundary on screen. PDF export still produces crisp pages via
 * the existing print CSS (`break-inside: avoid` on bullets etc.)
 * Real "content respects page boundaries on screen" would require
 * a ProseMirror widget-decoration plugin — Phase 5.5 if needed.
 *
 * Geometry mirrors `Paper.tsx` exactly so on-screen and PDF stay
 * in sync:
 *
 *   Paper:        8.5" × 11"
 *   Top margin:   1.00"
 *   Right:        0.70"
 *   Bottom:       1.51"
 *   Left:         2.85"
 *   Content area: 5.00" × 8.49"
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { OJB_OFFICES, OJB_URL_LABEL } from '@ojb/shared';

// All measurements in CSS pixels at 96 dpi. These MUST stay in
// lockstep with the .docx + .pdf templates so editor page breaks
// fall in the same place as the exports.
//
// Pilot WYSIWYG calibration:
//   - Page 1 top: 1.0in (room for the large letterhead logo)
//   - Pages 2+ top: 0.5in (just below the smaller running-header logo)
//   - All pages bottom: 0.5in
//   - Left: 2.85in (chrome strip lives here)
//   - Right: 0.7in
//
// PAGE_TOP_FIRST and PAGE_TOP_CONTINUATION are used by the
// content-padding logic below: the body padding-top stays at 1in
// (page-1 letterhead room), and the per-page slot rendering then
// only needs 0.5in inset on continuation pages. Bottom is 0.5in
// uniformly.
export const PAGE_HEIGHT_PX = 11 * 96; // 1056
const PAGE_WIDTH_PX = 8.5 * 96; // 816
const TOP_MARGIN_PX = 1 * 96; // 96 — first page only (letterhead)
// 0.5" — matches .docx PAGE.margin.bottom and .pdf @page bottom
// 1:1 per pilot WYSIWYG spec. The page-number widget moved to
// 0.3" from page edge (below content area) so it doesn't collide
// with the last content line. Must stay in sync with
// Pagination.ts BOTTOM_MARGIN_PX_DEFAULT.
const BOTTOM_MARGIN_PX = 0.5 * 96; // 48
const LEFT_MARGIN_PX = 2.85 * 96; // ~274
const RIGHT_MARGIN_PX = 0.7 * 96; // ~67

// Visible gap between pages (gray "desk" space showing through).
// Makes pages feel like discrete sheets — Word print-preview
// style — instead of one continuous strip of paper. The
// Pagination plugin imports this constant so its spacers push
// content past the gap and onto the next page's content area.
export const PAGE_GAP_PX = 28;
// One page slot = the page itself (PAGE_HEIGHT) + the gap below
// it. Used everywhere we need to convert between view.dom Y and
// "which page is this on."
export const PAGE_SLOT_PX = PAGE_HEIGHT_PX + PAGE_GAP_PX; // 1084

interface Props {
  children: ReactNode;
  /**
   * When provided, this callback fires every time the page count
   * recomputes. Lets the parent surface the page count in chrome
   * outside the paper (e.g. the zoom toolbar).
   */
  onPageCountChange?: (pageCount: number) => void;
  /** Hide the brand chrome (logo + office ribbon). Used for the
   *  loading skeleton state. */
  hideMarginDecoration?: boolean;
  /** Meeting title — rendered in the upper-left margin of pages
   *  2+ as a small running header (page 1 has the full
   *  letterhead with logo + office ribbon and doesn't need it). */
  meetingTitle?: string;
  /** Meeting date (ISO YYYY-MM-DD) — rendered alongside the
   *  title on pages 2+. */
  meetingDate?: string;
  /** Show the bottom-right "Page X / Y" label on each page. */
  showPageNumber?: boolean;
}

export interface PaginatedPaperHandle {
  /** Current page count, computed from content height. */
  getPageCount: () => number;
}

export const PaginatedPaper = forwardRef<PaginatedPaperHandle, Props>(
  function PaginatedPaper(
    {
      children,
      onPageCountChange,
      hideMarginDecoration,
      meetingTitle,
      meetingDate,
      showPageNumber = true,
    },
    ref,
  ) {
    const contentRef = useRef<HTMLDivElement>(null);
    const [pageCount, setPageCount] = useState(1);

    // Re-measure on every content height change. ResizeObserver fires
    // whenever the content node's box size changes — covers
    // typing, atom updates, image loads, font reflows, etc.
    //
    // Bug fix (Phase 6 hotfix): the divisor here MUST be
    // PAGE_HEIGHT_PX (full 11" page), not CONTENT_HEIGHT_PER_PAGE_PX
    // (the 8.49" usable area). The contentRef's scrollHeight
    // includes our paddings, which represent visual top/bottom
    // margin gaps that DO consume page height. Dividing by 815
    // instead of 1056 over-counted pages, which combined with the
    // (now removed) minHeight on contentRef created a runaway
    // feedback loop — every render grew the sheet, which grew
    // scrollHeight, which grew pageCount, etc. (One reproducer
    // produced 26,919 pages from a 496-word doc.)
    useLayoutEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      // Hysteresis: scrollHeight that lands within a few pixels
      // of an N * PAGE_SLOT_PX boundary flips pageCount on tiny
      // sub-pixel reflows (font load, decoration insertion,
      // chrome re-render). When that happens we visibly oscillate
      // between 9 and 10 pages mid-edit. Require a buffer of
      // HYSTERESIS_PX before we'll DECREMENT pageCount; INCREMENT
      // is allowed at any overflow above the strict ceiling.
      const HYSTERESIS_PX = 24;
      const recompute = () => {
        const h = el.scrollHeight;
        setPageCount((prev) => {
          // Increment if content has clearly grown past the
          // current page allotment.
          const ceilCount = Math.max(1, Math.ceil(h / PAGE_SLOT_PX));
          if (ceilCount > prev) return ceilCount;
          // Decrement only if content has shrunk WELL below the
          // boundary for the current pageCount — protects against
          // micro-reflow flicker when scrollHeight sits right at
          // a boundary.
          const dropThreshold = (prev - 1) * PAGE_SLOT_PX - HYSTERESIS_PX;
          if (prev > 1 && h <= dropThreshold) {
            return Math.max(1, Math.ceil(h / PAGE_SLOT_PX));
          }
          return prev;
        });
      };
      recompute();
      const ro = new ResizeObserver(recompute);
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    // Surface the page count outward so consumers (toolbar) can
    // display it. Fires on every change.
    useEffect(() => {
      onPageCountChange?.(pageCount);
    }, [pageCount, onPageCountChange]);

    useImperativeHandle(ref, () => ({ getPageCount: () => pageCount }), [
      pageCount,
    ]);

    // Total visual sheet height — N pages worth of slot, minus
    // one trailing gap (no gray strip below the last page). Always
    // at least one page-slot tall.
    const sheetHeightPx =
      pageCount * PAGE_SLOT_PX - (pageCount > 0 ? PAGE_GAP_PX : 0);

    return (
      // Outer wrapper has the gray "desk" background. Each page is
      // an independently-shadowed white sheet placed inside, with
      // PAGE_GAP_PX of gray space between them.
      <article
        className="relative mx-auto bg-cloud text-charcoal"
        style={{
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
          width: PAGE_WIDTH_PX,
          minHeight: sheetHeightPx,
        }}
      >
        {/* White page sheets — N stacked rectangles, each at its
            own page slot, separated by PAGE_GAP_PX of gray. Each
            gets its own shadow + ring so it reads as a discrete
            piece of paper, matching Word's print preview. */}
        {Array.from({ length: pageCount }, (_, i) => (
          <div
            key={i}
            aria-hidden
            className="absolute left-0 bg-white shadow-[0_2px_16px_rgba(0,0,0,0.15)] ring-1 ring-black/5"
            style={{
              top: i * PAGE_SLOT_PX,
              width: PAGE_WIDTH_PX,
              height: PAGE_HEIGHT_PX,
            }}
          />
        ))}

        {/* Brand chrome — page 0 gets the full letterhead (logo +
            office ribbon). Pages 1+ get a slimmer continuation
            header (logo + meeting title + date) in the same
            left margin strip so the document still reads as
            an OJB doc on every page. */}
        {!hideMarginDecoration &&
          Array.from({ length: pageCount }, (_, i) => (
            <PageChrome
              key={i}
              pageIndex={i}
              meetingTitle={meetingTitle}
              meetingDate={meetingDate}
            />
          ))}

        {/* Per-page bottom-right "Page X / Y" label. */}
        {showPageNumber &&
          Array.from({ length: pageCount }, (_, i) => (
            <PageNumber
              key={i}
              pageIndex={i}
              pageNumber={i + 1}
              total={pageCount}
            />
          ))}

        {/* "Page X of Y" labels centered in each gap (between
            page i and page i+1). */}
        {pageCount > 1 &&
          Array.from({ length: pageCount - 1 }, (_, i) => (
            <PageGapLabel
              key={i}
              y={(i + 1) * PAGE_SLOT_PX - PAGE_GAP_PX}
              page={i + 2}
              total={pageCount}
            />
          ))}

        {/* Editable content column — overlays the white page
            sheets so the editor's text + interactions sit on
            top. z-10 keeps it above the page backgrounds and
            chrome. */}
        <div
          ref={contentRef}
          className="relative z-10"
          style={{
            paddingTop: TOP_MARGIN_PX,
            paddingLeft: LEFT_MARGIN_PX,
            paddingRight: RIGHT_MARGIN_PX,
            paddingBottom: BOTTOM_MARGIN_PX,
          }}
        >
          {children}
        </div>
      </article>
    );
  },
);

/** "2026-04-23" → "April 23, 2026". Avoids the Date constructor
 *  so we don't drift across timezones. */
function formatHumanDate(iso: string | undefined | null): string {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const y = Number.parseInt(parts[0], 10);
  const m = Number.parseInt(parts[1], 10);
  const d = Number.parseInt(parts[2], 10);
  if (!y || !m || !d || m < 1 || m > 12) return iso;
  return `${months[m - 1]} ${d}, ${y}`;
}

/** Per-page brand chrome.
 *  Page 0 → full letterhead: logo (1.3"), office-locations
 *           ribbon at the bottom of the left margin strip.
 *  Pages 1+ → slimmer continuation header: smaller logo (1.0")
 *           plus meeting title + date, all in the same left
 *           margin strip. No ribbon (that's letterhead-only).
 *  Both variants stay inside the left margin strip (left: 0.7",
 *  width: 1.5") so they never overlap the editor's content
 *  column. */
function PageChrome({
  pageIndex,
  meetingTitle,
  meetingDate,
}: {
  pageIndex: number;
  meetingTitle?: string;
  meetingDate?: string;
}) {
  const isFirstPage = pageIndex === 0;
  return (
    <div
      className="pointer-events-none absolute left-0 right-0"
      style={{
        top: pageIndex * PAGE_SLOT_PX,
        height: PAGE_HEIGHT_PX,
      }}
      aria-hidden={!isFirstPage}
    >
      <img
        src="/ojb-logo-charcoal.png"
        alt={isFirstPage ? 'OJB' : ''}
        className="select-none"
        style={{
          position: 'absolute',
          top: '0.5in',
          left: '0.7in',
          // Smaller logo on continuation pages so the running
          // header reads as supporting chrome, not letterhead.
          width: isFirstPage ? '1.3in' : '1in',
          height: 'auto',
        }}
      />

      {isFirstPage ? (
        // Office-locations ribbon — letterhead only. Pine borders +
        // 12pt vertical padding match the .docx + .pdf ribbon
        // styling so all three surfaces look identical.
        <div
          style={{
            position: 'absolute',
            left: '0.7in',
            bottom: '0.8in',
            width: '1.5in',
            borderTop: '1pt solid #375542',
            borderBottom: '1pt solid #375542',
            padding: '12pt 0',
          }}
        >
          <div className="mb-1.5 text-[7pt] italic tracking-wider text-pine">
            {OJB_URL_LABEL}
          </div>
          <ul className="space-y-1">
            {OJB_OFFICES.map((o) => (
              <li key={o.city}>
                <div className="text-[7pt] font-bold tracking-wider text-pine">
                  {o.city}
                </div>
                <div className="text-[5pt] italic uppercase tracking-wider text-pine">
                  {o.state}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        // Continuation running header — title + date below the
        // smaller logo. Sits at top: ~1.6" (just below the 1"
        // logo + 0.5" top offset + small gap).
        <div
          style={{
            position: 'absolute',
            top: '1.6in',
            left: '0.7in',
            width: '1.5in',
          }}
          className="select-none"
        >
          {meetingTitle && (
            <div className="text-[8pt] font-bold leading-snug tracking-wide text-charcoal">
              {meetingTitle}
            </div>
          )}
          {meetingDate && (
            <div className="mt-0.5 text-[7pt] italic tracking-wider text-pine">
              {formatHumanDate(meetingDate)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Bottom-right "Page X / Y" — small, lives at right: 0.7",
 *  bottom: 0.5" of each page (matches the legacy Paper.tsx
 *  position). pointer-events-none so it doesn't block clicks
 *  meant for editable content above it. */
function PageNumber({
  pageIndex,
  pageNumber,
  total,
}: {
  pageIndex: number;
  pageNumber: number;
  total: number;
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute z-20 select-none text-[7pt] tracking-wider text-charcoal"
      style={{
        // 0.3" up from the page's bottom edge — sits in the lower
        // half of the bottom margin band (margin = 0.5"), well
        // below the content area (which ends at 0.5" from page
        // edge). Mirrors how Word renders a footer paragraph
        // toward the bottom of the footer zone, not at its top.
        top: pageIndex * PAGE_SLOT_PX + PAGE_HEIGHT_PX - 0.3 * 96,
        right: '0.7in',
      }}
    >
      Page {pageNumber} of {total}
    </div>
  );
}

/** Small "Page X of Y" pill centered in the gray gap between two
 *  pages. The gap itself (cloud-colored "desk" space) makes the
 *  page boundary obvious; this label just confirms which page
 *  comes next. pointer-events-none so the editor still receives
 *  clicks meant for content. */
function PageGapLabel({
  y,
  page,
  total,
}: {
  y: number;
  page: number;
  total: number;
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute left-0 right-0 z-20 flex items-center justify-center"
      style={{ top: y, height: PAGE_GAP_PX }}
    >
      <div className="rounded-full border border-pine/20 bg-white px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-pine shadow-sm">
        Page {page} of {total}
      </div>
    </div>
  );
}
